import { useCreatorAccess } from './CreatorAccessRoute';
import { useEffect, useMemo, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useSelector, useDispatch } from "react-redux";
import { FaShoppingCart, FaUser, FaBars, FaTimes, FaChevronDown } from "react-icons/fa";
import {
  useLogoutMutation,
  useCheckUserCreatorQuery,
  useUpdatePreferredCurrencyMutation,
} from "../slices/usersApiSlice.js";
import { logout, setCredentials } from "../slices/authSlice.js";
import SearchBox from "./SearchBox";
import logo from "../assets/logo2.png";
import CategoryMenu from "./CategoryMenu";
import { useGlobal } from "../contexts/globalContext";
import { toast } from "sonner";

function Header() {
  const { cartItems } = useSelector((state) => state.cart);
  const { userInfo } = useSelector((state) => state.auth);
  const { hasBecome, setHasBecome } = useGlobal();
  const [mobileOpen, setMobileOpen] = useState(false);
  const [accountOpen, setAccountOpen] = useState(false);
  const [adminOpen, setAdminOpen] = useState(false);
  const [selectedCurrency, setSelectedCurrency] = useState(userInfo?.preferredCurrency || "USD");
  const [updatePreferredCurrency, { isLoading: currencyUpdating }] = useUpdatePreferredCurrencyMutation();

  const creatorAccess = useCreatorAccess();
  const { data: isCreator, refetch } = useCheckUserCreatorQuery(userInfo?._id, {
    skip: !userInfo,
  });

  const dispatch = useDispatch();
  const navigate = useNavigate();
  const [logoutApiCall] = useLogoutMutation();
  const currencyOptions = [
    { value: "USD", label: "USD" },
    { value: "EUR", label: "EUR" },
    { value: "GBP", label: "GBP" },
  ];

  const cartCount = useMemo(
    () => cartItems.reduce((a, c) => a + c.qty, 0),
    [cartItems]
  );

  async function logoutHandler() {
    try {
      await logoutApiCall().unwrap();
      dispatch(logout());
      navigate("/login");
    } catch (err) {
      console.error(err);
    }
  }

  useEffect(() => {
    if (hasBecome) {
      refetch();
      setHasBecome(false);
    }
  }, [hasBecome, refetch, setHasBecome]);

  useEffect(() => {
    setSelectedCurrency(userInfo?.preferredCurrency || "USD");
  }, [userInfo?.preferredCurrency]);

  const handleCurrencyChange = async (nextCurrency) => {
    if (!userInfo || !nextCurrency || nextCurrency === userInfo?.preferredCurrency) return;
    setSelectedCurrency(nextCurrency);
    try {
      const res = await updatePreferredCurrency(nextCurrency).unwrap();
      dispatch(
        setCredentials({
          ...userInfo,
          preferredCurrency: res?.preferredCurrency || nextCurrency,
        })
      );
      toast.success(`Currency set to ${res?.preferredCurrency || nextCurrency}`);
    } catch (err) {
      setSelectedCurrency(userInfo?.preferredCurrency || "USD");
      toast.error(err?.data?.message || "Failed to update currency");
    }
  };

  return (
    <header className="sticky top-0 z-50 border-b border-slate-200 bg-white/90 backdrop-blur">
      <div className="mx-auto flex max-w-[1400px] items-center gap-3 px-4 py-3 md:px-6">
        <Link to="/" className="flex items-center gap-2 text-slate-900 no-underline">
          <img src={logo} alt="Practidex logo" className="h-10 w-10 object-contain" />
          <span className="leading-tight">
            <span className="block text-lg font-semibold tracking-tight">
              Practi<span className="text-sky-700">dex</span>
            </span>
            <span className="block text-[11px] font-medium text-slate-500">by El Amine Morabet</span>
          </span>
        </Link>

        <div className="hidden flex-1 md:block">
          <SearchBox />
        </div>

        <nav className="hidden items-center gap-2 md:flex">
          <Link
            to="/cart"
            className="inline-flex items-center gap-2 rounded-lg border border-slate-200 px-3 py-2 text-sm font-medium text-slate-700 no-underline hover:bg-slate-50"
          >
            <FaShoppingCart />
            <span>Cart</span>
            {cartCount > 0 && (
              <span className="rounded-full bg-emerald-500 px-2 py-0.5 text-xs font-semibold text-white">
                {cartCount}
              </span>
            )}
          </Link>

          {userInfo ? (
            <>
              <div className="inline-flex items-center rounded-lg border border-slate-200 bg-white px-2 py-1.5">
                <select
                  value={selectedCurrency}
                  onChange={(e) => handleCurrencyChange(e.target.value)}
                  disabled={currencyUpdating}
                  className="min-w-[74px] rounded-md border border-slate-200 bg-slate-50 px-2 py-1 text-xs font-semibold text-slate-700 outline-none focus:border-sky-300 focus:ring-2 focus:ring-sky-100 disabled:cursor-not-allowed disabled:opacity-60"
                  aria-label="Preferred currency"
                  title="Preferred currency"
                >
                  {currencyOptions.map((entry) => (
                    <option key={entry.value} value={entry.value}>
                      {entry.label}
                    </option>
                  ))}
                </select>
              </div>

              <div
                className="relative"
                onMouseLeave={() => {
                  setAccountOpen(false);
                }}
              >
                <button
                  type="button"
                  onClick={() => {
                    setAccountOpen((prev) => !prev);
                    setAdminOpen(false);
                  }}
                  className="inline-flex items-center gap-2 rounded-lg border border-slate-200 px-3 py-2 text-sm font-medium text-slate-700 hover:bg-slate-50"
                >
                  <span>{userInfo.name}</span>
                  <FaChevronDown className="text-xs" />
                </button>
                {accountOpen && (
                  <div className="absolute right-0 top-full z-20 w-52 pt-2">
                    <div className="rounded-xl border border-slate-200 bg-white p-1 shadow-lg">
                      <Link className="block rounded-lg px-3 py-2 text-sm text-slate-700 no-underline hover:bg-slate-50" to="/profile" onClick={() => setAccountOpen(false)}>Profile</Link>
                      <Link className="block rounded-lg px-3 py-2 text-sm text-slate-700 no-underline hover:bg-slate-50" to="/purchases" onClick={() => setAccountOpen(false)}>Purchase History</Link>
                      <Link className="block rounded-lg px-3 py-2 text-sm text-slate-700 no-underline hover:bg-slate-50" to="/wishlist" onClick={() => setAccountOpen(false)}>Wishlist</Link>
                      <Link className="block rounded-lg px-3 py-2 text-sm text-slate-700 no-underline hover:bg-slate-50" to="/help" onClick={() => setAccountOpen(false)}>Help & FAQ</Link>
                      {(isCreator ? creatorAccess.tools : creatorAccess.upgrades) && (isCreator ? (
                        <Link className="block rounded-lg px-3 py-2 text-sm text-slate-700 no-underline hover:bg-slate-50" to="/profile/creator" onClick={() => setAccountOpen(false)}>
                          Creator Page
                        </Link>
                      ) : (
                        <Link className="block rounded-lg px-3 py-2 text-sm text-slate-700 no-underline hover:bg-slate-50" to="/profile/become-a-creator" onClick={() => setAccountOpen(false)}>
                          Become a Creator
                        </Link>
                      ))}
                      <button
                        type="button"
                        onClick={logoutHandler}
                        className="mt-1 w-full rounded-lg px-3 py-2 text-left text-sm text-rose-600 hover:bg-rose-50"
                      >
                        Logout
                      </button>
                    </div>
                  </div>
                )}
              </div>
            </>
          ) : (
            <Link
              to="/login"
              className="inline-flex items-center gap-2 rounded-lg border border-slate-200 px-3 py-2 text-sm font-medium text-slate-700 no-underline hover:bg-slate-50"
            >
              <FaUser />
              <span>Sign In</span>
            </Link>
          )}

          {userInfo?.isAdmin && (
            <div
              className="relative"
              onMouseLeave={() => {
                setAdminOpen(false);
              }}
            >
              <button
                type="button"
                onClick={() => {
                  setAdminOpen((prev) => !prev);
                  setAccountOpen(false);
                }}
                className="inline-flex items-center gap-2 rounded-lg border border-slate-200 px-3 py-2 text-sm font-medium text-slate-700 hover:bg-slate-50"
              >
                <span>Admin</span>
                <FaChevronDown className="text-xs" />
              </button>
              {adminOpen && (
                <div className="absolute right-0 top-full z-20 w-44 pt-2">
                  <div className="rounded-xl border border-slate-200 bg-white p-1 shadow-lg">
                    <Link className="block rounded-lg px-3 py-2 text-sm text-slate-700 no-underline hover:bg-slate-50" to="/admin/productlist">Products</Link>
                    <Link className="block rounded-lg px-3 py-2 text-sm text-slate-700 no-underline hover:bg-slate-50" to="/admin/userlist">Users</Link>
                    <Link className="block rounded-lg px-3 py-2 text-sm text-slate-700 no-underline hover:bg-slate-50" to="/admin/orderlist">Orders</Link>
                    <Link className="block rounded-lg px-3 py-2 text-sm text-slate-700 no-underline hover:bg-slate-50" to="/admin/operations">Operations</Link>
                  </div>
                </div>
              )}
            </div>
          )}
        </nav>

        <Link to="/cart" aria-label={`Cart (${cartCount})`} onClick={() => setMobileOpen(false)}
          className="ml-auto inline-flex min-h-[44px] items-center gap-1 rounded-lg border border-slate-200 px-2 text-slate-700 md:hidden">
          <FaShoppingCart /> <span className="text-sm">{cartCount}</span>
        </Link>
        <button
          type="button"
          className="inline-flex min-h-[44px] min-w-[44px] items-center justify-center rounded-lg border border-slate-200 p-2 text-slate-700 md:hidden"
          onClick={() => setMobileOpen((prev) => !prev)}
          aria-label="Toggle navigation"
        >
          {mobileOpen ? <FaTimes /> : <FaBars />}
        </button>
      </div>

      <div className="px-4 pb-3 md:hidden"><SearchBox /></div>

      {mobileOpen && (
        <div className="border-t border-slate-200 px-4 py-3 md:hidden">
          <div className="grid gap-2">
            {userInfo ? (
              <>
                <div className="rounded-lg border border-slate-200 px-3 py-2">
                  <label htmlFor="mobileCurrency" className="mb-1 block text-xs font-medium uppercase tracking-wide text-slate-500">
                    Currency
                  </label>
                  <select
                    id="mobileCurrency"
                    value={selectedCurrency}
                    onChange={(e) => handleCurrencyChange(e.target.value)}
                    disabled={currencyUpdating}
                    className="w-full rounded-lg border border-slate-200 bg-slate-50 px-2.5 py-2 text-sm font-medium text-slate-700 outline-none focus:border-sky-300 focus:ring-2 focus:ring-sky-100 disabled:cursor-not-allowed disabled:opacity-60"
                  >
                    {currencyOptions.map((entry) => (
                      <option key={entry.value} value={entry.value}>
                        {entry.label}
                      </option>
                    ))}
                  </select>
                </div>
                <Link className="rounded-lg border border-slate-200 px-3 py-2 text-sm font-medium text-slate-700 no-underline" to="/profile" onClick={() => setMobileOpen(false)}>Profile</Link>
                <Link className="rounded-lg border border-slate-200 px-3 py-2 text-sm font-medium text-slate-700 no-underline" to="/purchases" onClick={() => setMobileOpen(false)}>Purchase History</Link>
                <Link className="rounded-lg border border-slate-200 px-3 py-2 text-sm font-medium text-slate-700 no-underline" to="/wishlist" onClick={() => setMobileOpen(false)}>Wishlist</Link>
                <Link className="rounded-lg border border-slate-200 px-3 py-2 text-sm font-medium text-slate-700 no-underline" to="/help" onClick={() => setMobileOpen(false)}>Help & FAQ</Link>
                {(isCreator ? creatorAccess.tools : creatorAccess.upgrades) && (isCreator ? (
                  <Link className="rounded-lg border border-slate-200 px-3 py-2 text-sm font-medium text-slate-700 no-underline" to="/profile/creator" onClick={() => setMobileOpen(false)}>Creator Page</Link>
                ) : (
                  <Link className="rounded-lg border border-slate-200 px-3 py-2 text-sm font-medium text-slate-700 no-underline" to="/profile/become-a-creator" onClick={() => setMobileOpen(false)}>Become a Creator</Link>
                ))}
                {userInfo?.isAdmin && (
                  <>
                    <Link className="rounded-lg border border-slate-200 px-3 py-2 text-sm font-medium text-slate-700 no-underline" to="/admin/productlist" onClick={() => setMobileOpen(false)}>Admin Products</Link>
                    <Link className="rounded-lg border border-slate-200 px-3 py-2 text-sm font-medium text-slate-700 no-underline" to="/admin/userlist" onClick={() => setMobileOpen(false)}>Admin Users</Link>
                    <Link className="rounded-lg border border-slate-200 px-3 py-2 text-sm font-medium text-slate-700 no-underline" to="/admin/orderlist" onClick={() => setMobileOpen(false)}>Admin Orders</Link>
                    <Link className="rounded-lg border border-slate-200 px-3 py-2 text-sm font-medium text-slate-700 no-underline" to="/admin/operations" onClick={() => setMobileOpen(false)}>Admin Operations</Link>
                  </>
                )}
                <button
                  type="button"
                  onClick={() => {
                    logoutHandler();
                    setMobileOpen(false);
                  }}
                  className="rounded-lg border border-rose-200 px-3 py-2 text-left text-sm font-medium text-rose-600"
                >
                  Logout
                </button>
              </>
            ) : (
              <Link className="rounded-lg border border-slate-200 px-3 py-2 text-sm font-medium text-slate-700 no-underline" to="/login" onClick={() => setMobileOpen(false)}>
                Sign In
              </Link>
            )}
          </div>
        </div>
      )}
      <CategoryMenu />
    </header>
  );
}

export default Header;
